<!-- Home -->
<script setup lang="ts">
import { getHomeAnnouncement } from '~/apis/home'
import RecommendedSongs from '~/components/home/RecommendedSongs.vue'
import TrendingSongs from '~/components/home/TrendingSongs.vue'
import NewSongs from '~/components/home/NewSongs.vue'
import Shuffle from '~/assets/icons/shuffle.png'

const router = useRouter()
const authStore = useAuthStore()
const loginStore = useLoginStore()
const playerStore = usePlayerStore()
const { showErrorToast } = useToastHandler()

const homePrompt = ref('')

const randomDescriptions = [
  'A gentle rain tapping against a Tokyo afternoon windowpane, carrying subtle urban loneliness and tranquility',
  'The slow breathing of ocean tides under a moonlit night, with basslines pulsing like deep-sea heartbeats',
  'Warm nostalgia feeling like discovering old photographs in an attic, filled with dust, smiles and faded summers',
  'The tension and release of a gathering storm finally breaking into torrential rain, intense yet cathartic',
  'Bittersweet melancholy of an abandoned amusement park at dusk, where laughter echoes in silent spaces',
  'Floating through clouds made of crystal wind chimes, each movement creating delicate harmonic patterns',
  'Dancing shadows in a candlelit cathedral, where light and darkness compose their own sacred symphony',
  'Midnight taxi ride through neon-drenched city streets, with reflections blurring into liquid color',
  'Ancient silk road caravan moving through desert winds, carrying spices, stories and string instruments',
  'Victorian ghost waltz in a ballroom of mirrors, where reflections remember what bodies have forgotten',
  'Sun-warmed wooden instruments in an empty barn, carrying the memory of hands and heartbeats',
  'The weight of silence between heartbeats, amplified until it becomes its own kind of music'
]

const fillRandomDescription = () => {
  if (!randomDescriptions.length) return
  const index = Math.floor(Math.random() * randomDescriptions.length)
  homePrompt.value = randomDescriptions[index] ?? ''
}

const buildGenerateRedirectPath = (prompt: string) => {
  const params = new URLSearchParams({ autogen: '1', description: prompt })
  return `/generate?${params.toString()}`
}

// banner 相关
const bannerVisibility = ref(true)
const toggleBannerVisibility = () => {
  bannerVisibility.value = !bannerVisibility.value
}
const bannerValue = ref('')
const initBannerValue = async () => {
  const res = await getHomeAnnouncement({ key: 'hw_home_banner' })
  bannerValue.value = res?.data?.rows[0]?.value || ''
}

const handleGenerationClick = () => {
  const prompt = homePrompt.value.trim()
  if (!prompt) {
    showErrorToast('Please enter a song description')
    return
  }

  const redirectPath = buildGenerateRedirectPath(prompt)
  if (!authStore.isAuthenticated) {
    if (import.meta.client) {
      sessionStorage.setItem('redirectAfterLogin', redirectPath)
    }
    loginStore.openLoginModal('SignIn')
    return
  }

  router.push(redirectPath)
}

onMounted(() => {
  initBannerValue()
})

const step1Ref = ref<HTMLElement | null>(null)
const step2Ref = ref<HTMLElement | null>(null)
const step3Ref = ref<HTMLElement | null>(null)

const stepLine1Width = ref(0)
const stepLine2Width = ref(0)
const stepLine1Left = ref(0)
const stepLine2Right = ref(0)
const isDesktop = ref(false)

const updateStepLineWidths = () => {
  if (!import.meta.client) return

  isDesktop.value = window.innerWidth >= 768

  const w1 = step1Ref.value?.getBoundingClientRect().width ?? 0
  const w2 = step2Ref.value?.getBoundingClientRect().width ?? 0
  const w3 = step3Ref.value?.getBoundingClientRect().width ?? 0

  const extra = isDesktop.value ? 32 : 16

  stepLine1Width.value = Math.max(0, (w1 / 2 - 14) + (w2 / 2 - 14)) + extra
  stepLine2Width.value = Math.max(0, (w2 / 2 - 14) + (w3 / 2 - 14)) + extra

  stepLine1Left.value = (w1 / 2) + 14
  stepLine2Right.value = (w3 / 2) + 14
}

let stepResizeObserver: ResizeObserver | null = null
const handleWindowResize = () => updateStepLineWidths()

const handleHomePromptEnter = (event: KeyboardEvent) => {
  if (event.shiftKey) {
    return
  }
  event.preventDefault()
  handleGenerationClick()
}

onMounted(async () => {
  await nextTick()
  updateStepLineWidths()

  window.addEventListener('resize', handleWindowResize)

  if (typeof ResizeObserver !== 'undefined') {
    stepResizeObserver = new ResizeObserver(() => updateStepLineWidths())
    if (step1Ref.value) stepResizeObserver.observe(step1Ref.value)
    if (step2Ref.value) stepResizeObserver.observe(step2Ref.value)
    if (step3Ref.value) stepResizeObserver.observe(step3Ref.value)
  }
})

onBeforeUnmount(() => {
  if (stepResizeObserver) {
    stepResizeObserver.disconnect()
    stepResizeObserver = null
  }
  if (import.meta.client) {
    window.removeEventListener('resize', handleWindowResize)
  }
})

const title = 'XMUSIO – AI Music Generator | Create Original Music with AI'
const description = 'XMUSIO is an AI music generator that helps you create original, royalty-free music in seconds. Generate, customize, and manage your music effortlessly.'

useSeoMeta({
  title,
  description
})
</script>

<template>
  <div class="flex w-full h-full font-['Roboto'] bg-[linear-gradient(180deg,#64647E_-22.99%,#131318_14.96%)]">
    <div :class="`flex-1 min-w-0 h-full ${playerStore.currentSong ? 'pb-[80px]' : 'pb-0'} md:pt-[16px] overflow-y-auto transition-none md:transition-all`">
      <!-- banner 内容 -->
      <div
        :class="[
          'relative min-h-0 md:mx-[16px] transition-all duration-300 ease-in-out overflow-hidden',
          bannerVisibility
            ? 'opacity-100 md:mb-[32px] max-h-[320px]'
            : 'opacity-0 mb-[0px] max-h-0'
        ]"
      >
        <!-- 关闭按钮 -->
        <div
          class="absolute top-[10px] right-[10px] flex items-center justify-center size-[34px] cursor-pointer z-20
          before:content-[''] before:absolute before:w-[20px] before:h-[2px] before:bg-[#8888A1] hover:before:bg-[#E7E7EC] before:rotate-45 before:rounded-[2px]
          after:content-[''] after:absolute after:w-[20px] after:h-[2px] after:bg-[#8888A1] hover:after:bg-[#E7E7EC] after:-rotate-45 after:rounded-[2px]"
          @click="toggleBannerVisibility"
        />
        <ClientOnly>
          <div v-html="bannerValue" />
          <template #fallback>
            <div class="w-full" />
          </template>
        </ClientOnly>
      </div>

      <!-- 生成跳转按钮 -->
      <div class="flex flex-col items-center p-[16px] md:my-[16px] bg-[#262635] md:bg-transparent">
        <h1 class="text-[20px] md:text-[32px] text-[#D9D9D9] text-center font-bold leading-[1] shadow-[0px_7px_5px__0px_#00000030]">
          Start Making Music in 3 Easy Steps
        </h1>
        <div class="relative flex items-start justify-center gap-x-[16px] md:gap-x-[32px] mt-[16px] md:mt-[32px]">
          <div
            ref="step1Ref"
            class="flex flex-col items-center gap-y-[4px]"
          >
            <div class="flex items-center justify-center size-[20px] text-[14px] text-[#B8B8C7] font-bold bg-[#383847] border border-[#8888A1] rounded-full">
              1
            </div>
            <span class="text-[12px] md:text-[14px] text-[#B8B8C7] text-center leading-[1]">
              Enter song description
            </span>
          </div>

          <div
            class="absolute left-[84px] w-[90px] md:w-[124px] h-[1px] bg-[#5E5E77] mt-[10px] opacity-60"
            :style="{ width: `${stepLine1Width}px`, left: `${stepLine1Left}px` }"
          />

          <div
            ref="step2Ref"
            class="flex flex-col items-center gap-y-[4px]"
          >
            <div class="flex items-center justify-center size-[20px] text-[14px] text-[#B8B8C7] font-bold bg-[#383847] border border-[#8888A1] rounded-full">
              2
            </div>
            <span class="text-[12px] md:text-[14px] text-[#B8B8C7] text-center leading-[1]">
              Click “Generate”
            </span>
          </div>

          <div
            class="absolute right-[62px] w-[76px] md:w-[102px] h-[1px] bg-[#5E5E77] mt-[10px] opacity-60"
            :style="{ width: `${stepLine2Width}px`, right: `${stepLine2Right}px` }"
          />

          <div
            ref="step3Ref"
            class="flex flex-col items-center gap-y-[4px]"
          >
            <div class="flex items-center justify-center size-[20px] text-[14px] text-[#27C840] font-bold bg-[#34C75933] border border-[#34C759] rounded-full">
              <UIcon
                name="material-symbols:check-rounded"
                class="size-[18px] font-bold"
              />
            </div>
            <span class="text-[12px] md:text-[14px] text-[#27C840] text-center leading-[1]">
              AI does the rest
            </span>
          </div>
        </div>

        <div class="flex flex-col md:flex-row items-center gap-x-[8px] w-full max-w-[626px] mt-[16px] md:mt-[32px]">
          <div class="flex items-center flex-1 w-full border border-[#5E5E77] rounded-[8px] px-[6px] md:px-[12px] py-[5px]">
            <div
              class="size-[32px] flex items-center justify-center rounded-[4px] hover:bg-[#2F2F3A] mr-[4px] md:mr-[12px] transition-colors cursor-pointer"
              @click="fillRandomDescription"
            >
              <img
                class="size-[32px]"
                :src="Shuffle"
                alt=""
              >
            </div>
            <input
              v-if="isDesktop"
              v-model="homePrompt"
              class="flex-1 bg-transparent outline-none text-[14px] text-[#E7E7EC] placeholder:text-[#B8B8C7]"
              placeholder="Try: Upbeat synth-pop with a nostalgic 80s vibe and a catchy melody"
              @keydown.enter.prevent="handleGenerationClick"
            >
            <textarea
              v-else
              v-model="homePrompt"
              :rows="2"
              class="flex-1 bg-transparent outline-none text-[14px] text-[#E7E7EC] placeholder:text-[#B8B8C7] resize-none whitespace-pre-wrap"
              placeholder="Try: Upbeat synth-pop with a nostalgic 80s vibe and a catchy melody"
              @keydown.enter="handleHomePromptEnter"
            />
          </div>
          <button
            :class="`text-[16px] rounded-[8px] ${!!homePrompt.trim() ? 'text-[#F2D4FF] bg-[#BF2AFF] hover:bg-[#BF2AFF] active:bg-[#BF2AFF]': 'text-[#D880FF] bg-[#590080] hover:bg-[#590080] active:bg-[#590080]'} py-[10px] px-[16px] mt-[16px] md:mt-[0px] cursor-pointer`"
            @click="handleGenerationClick"
          >
            Generate
          </button>
        </div>
      </div>

      <!-- 推荐歌曲 -->
      <RecommendedSongs />

      <!-- 热门歌曲 -->
      <TrendingSongs />

      <!-- 最新歌曲 -->
      <NewSongs />
    </div>

    <!-- 右侧歌曲详情侧边栏 -->
    <Transition name="slide">
      <RightSongsDetail
        class="flex-[0_0_224px]"
        :song-actions-exclude-ids="['remix', 'download', 'edit_details', 'publish', 'move_to_trash']"
      />
    </Transition>
  </div>
</template>
